import React, { useState, createContext, useContext } from 'react'
import { Outlet } from 'react-router-dom'
import { motion } from 'framer-motion'
import Sidebar from './Sidebar'
import Header from './Header'
import ParticleSystem, { EnergyFlow, FloatingElements, ScanLines, HolographicGrid } from '../ui/ParticleSystem'
import StickyMediaPlayer from '../ui/StickyMediaPlayer'
import { pageTransition } from '../../lib/animations'
import { MediaPlayerProps } from '@/types/media'

// Context for managing sticky player globally
interface StickyPlayerContextType {
  showStickyPlayer: (file: File, config?: Partial<MediaPlayerProps['config']>) => void
  hideStickyPlayer: () => void
  isVisible: boolean
  currentFile: File | null
}

const StickyPlayerContext = createContext<StickyPlayerContextType | null>(null)

export const useStickyPlayer = () => {
  const context = useContext(StickyPlayerContext)
  if (!context) {
    throw new Error('useStickyPlayer must be used within a Layout component')
  }
  return context
}

const Layout: React.FC = () => {
  const [stickyPlayerState, setStickyPlayerState] = useState({
    isVisible: false,
    currentFile: null as File | null,
    config: {} as Partial<MediaPlayerProps['config']>
  })

  const showStickyPlayer = (file: File, config: Partial<MediaPlayerProps['config']> = {}) => {
    setStickyPlayerState({
      isVisible: true,
      currentFile: file,
      config: {
        mode: 'sticky',
        showWaveform: true,
        showVideoControls: false,
        ...config
      }
    })
  }

  const hideStickyPlayer = () => {
    setStickyPlayerState({
      isVisible: false,
      currentFile: null,
      config: {}
    })
  }

  const stickyPlayerContextValue: StickyPlayerContextType = {
    showStickyPlayer,
    hideStickyPlayer,
    isVisible: stickyPlayerState.isVisible,
    currentFile: stickyPlayerState.currentFile
  }

  return (
    <StickyPlayerContext.Provider value={stickyPlayerContextValue}>
      <div className="min-h-screen bg-cosmic-gradient relative overflow-hidden">
        {/* Cosmic Background Effects */}
        <ParticleSystem particleCount={30} />
        <EnergyFlow />
        <FloatingElements />
        <ScanLines />
        <HolographicGrid />
        
        {/* Main Layout */}
        <div className="relative z-10 flex min-h-screen">
          <Sidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <motion.main 
              className={`flex-1 p-6 relative transition-all duration-300 ${
                stickyPlayerState.isVisible ? 'pb-24' : ''
              }`}
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {/* Content Container with Glassmorphism */}
              <div className="relative">
                <Outlet />
              </div>
            </motion.main>
          </div>
        </div>
        
        {/* Ambient Lighting Effects */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-stellar-blue/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-nebula-purple/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-stellar-cyan/5 rounded-full blur-3xl" />
        </div>

        {/* Sticky Media Player */}
        {stickyPlayerState.currentFile && (
          <StickyMediaPlayer
            file={stickyPlayerState.currentFile}
            config={stickyPlayerState.config}
            isVisible={stickyPlayerState.isVisible}
            onClose={hideStickyPlayer}
            position="bottom"
          />
        )}
      </div>
    </StickyPlayerContext.Provider>
  )
}

export default Layout 